/**
 * Scheduling hooks for the specialist Schedules tab: the per-agent task list,
 * run history, and the update/pause/resume/cancel mutations.
 */

import type { SchedulePatch } from "@lifter/hub-ui/browser-shared";
import { hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AgentQueryOptions, useHubApi } from "./context";

export function useSchedulesQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.schedules(agentId),
    queryFn: () => api.schedules.list(agentId),
    enabled: options.enabled ?? true,
    refetchInterval: 5_000,
  });
}

export function useScheduleRunsQuery(
  agentId: string,
  scheduleId: string,
  options: AgentQueryOptions = {},
) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.scheduleRuns(agentId, scheduleId),
    queryFn: () => api.schedules.runs(agentId, scheduleId),
    enabled: (options.enabled ?? true) && Boolean(scheduleId),
    refetchInterval: 5_000,
  });
}

export function useScheduleRunTranscriptQuery(
  agentId: string,
  scheduleId: string,
  runId: number | undefined,
  options: AgentQueryOptions = {},
) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.scheduleRunTranscript(agentId, scheduleId, runId ?? 0),
    queryFn: () => api.schedules.transcript(agentId, scheduleId, runId ?? 0),
    enabled: (options.enabled ?? true) && runId !== undefined,
  });
}

export function useUpdateScheduleMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, patch }: { scheduleId: string; patch: SchedulePatch }) =>
      api.schedules.update(agentId, scheduleId, patch),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.schedules(agentId) });
      queryClient.invalidateQueries({
        queryKey: hubQueryKeys.scheduleRuns(agentId, variables.scheduleId),
      });
    },
  });
}

export function useScheduleActionMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      scheduleId,
      action,
    }: {
      scheduleId: string;
      action: "pause" | "resume" | "cancel";
    }) => api.schedules.action(agentId, scheduleId, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.schedules(agentId) });
    },
  });
}
